import React, { useMemo, useState } from 'react';
import {
  faDownload,
  faEye,
  faTrash,
  Icon,
  usePreviewModal,
  CustomColumns,
  CustomTable,
  DragUpload,
} from '@/ui/components';
import { StyledLinkContainer, StyledLink } from '@/ui/globalStyles';
import { removeExtension, timeConverter } from '@/utils/helpers';
import { ShippingPlan } from '@/types';
import { App, Form, Modal, Select, UploadFile } from 'antd';
import { acceptFiles, MAX_FILE_SIZE } from '@/utils/constants';
import { useTranslate } from '@/translations';
import {
  useShippingPlan,
  useUploadShippingPlan,
  useDeleteShippingPlan,
} from '@/api/ShippingPlan';
import { useAppCustomers } from '@/hooks';

export const AdminShippingPlanDataGrid = () => {
  const { t } = useTranslate();
  const { message } = App.useApp();
  const {
    userPermissions: { customers },
  } = useAppCustomers();
  const { openPreviewModal, RenderPreviewModal } = usePreviewModal();

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [customer, setCustomer] = useState<number>();
  const [file, setFile] = useState<ArrayBuffer | string | null>(null);
  const [fileName, setFileName] = useState('');
  const { data, isFetching } = useShippingPlan();
  const { mutateAsync: uploadMutateAsync, isPending: uploadLoading } =
    useUploadShippingPlan();
  const { mutate: deleteMutate } = useDeleteShippingPlan();

  const onCancel = () => {
    setCustomer(undefined);
    setFile(null);
    setFileName('');
    setShowUploadModal(false);
  };

  const uploadFile = async () => {
    if (!customer || !file || !fileName) {
      message.error('Customer, file and file name are required');
      return;
    }
    const res = await uploadMutateAsync({
      data: { shippingPlanFileBase64: file, fileName, customerId: customer },
    });
    if (!res.success) {
      message.error(
        res.message
          ? res.message.Message
          : 'An error occurred while uploading the file',
      );
      return;
    }
    onCancel();
  };

  const onFileChanged = (files: UploadFile[]) => {
    if (files.length === 0) {
      setFile(null);
      setFileName('');
      return;
    }

    const item = files[0].originFileObj as File;
    if (!item) return;

    const reader = new FileReader();
    reader.onload = ({ target }) => {
      if (target?.result) {
        setFile(target.result);
        setFileName(removeExtension(item.name));
      }
    };
    reader.readAsDataURL(item); // convert to base64 string
  };

  const columns: CustomColumns<ShippingPlan> = useMemo(
    () => [
      {
        dataIndex: 'customerName',
        title: 'Customer',
        width: 200,
      },
      { dataIndex: 'fileName', title: 'File Name' },
      {
        dataIndex: 'createdDate',
        title: 'Created Date',
        width: 180,
        render: (value) => timeConverter(value),
      },
      {
        dataIndex: 'fileUrl',
        title: 'File URL',
        width: 100,
        align: 'center',
        render: (value, item) => (
          <StyledLinkContainer>
            <a href={value} download={item.fileName} aria-label="Download">
              <Icon icon={faDownload} />
            </a>
            <StyledLink
              onClick={() =>
                openPreviewModal({ src: value, fileName: item.fileName })
              }
              aria-label="View"
            >
              <Icon icon={faEye} />
            </StyledLink>
            <StyledLink
              onClick={() => deleteMutate(item.documentId)}
              aria-label="Delete"
            >
              <Icon icon={faTrash} />
            </StyledLink>
          </StyledLinkContainer>
        ),
      },
    ],
    [deleteMutate, openPreviewModal],
  );

  return (
    <>
      <CustomTable
        dataSource={data?.entities || []}
        columns={columns}
        rowKey="documentId"
        loading={isFetching}
        onAdd={() => setShowUploadModal(true)}
      />

      <Modal
        open={showUploadModal}
        onCancel={onCancel}
        okText={t('upload')}
        onOk={uploadFile}
        confirmLoading={uploadLoading}
        footer={(_, { OkBtn }) => <OkBtn />}
        title={t('uploadShippingPlan')}
      >
        <Form size="large" labelCol={{ flex: '0 0 100px' }}>
          <Form.Item label={t('customer')} rules={[{ required: true }]}>
            <Select
              options={customers?.map((c) => ({
                label: c.customerName,
                value: c.customerId,
              }))}
              value={customer}
              onChange={setCustomer}
            />
          </Form.Item>
          <Form.Item label={t('file')}>
            <DragUpload
              accept={acceptFiles}
              maxFileSize={MAX_FILE_SIZE}
              showUploadBtn={false}
              onFileChanged={onFileChanged}
            />
          </Form.Item>
        </Form>
      </Modal>

      <RenderPreviewModal />
    </>
  );
};
